import axios, { AxiosHeaders } from "axios";
import type { AxiosRequestConfig } from "axios";

export interface SessionSnapshot {
  revision: number;
  token: string | null;
  refreshToken: string | null;
}
export interface SessionAdapter {
  snapshot(): SessionSnapshot;
  isResponse(data: unknown): boolean;
  accept(data: unknown, revision: number): boolean;
  clear(revision: number): void;
}
export type ApiRequest = <T = unknown>(
  options: AxiosRequestConfig,
  authenticated?: boolean,
) => Promise<T>;
export class SessionChangedError extends Error {
  constructor() {
    super("Die Sitzung wurde inzwischen geändert.");
  }
}

/** Each application supplies its storage/state adapter; refresh coordination is shared. */
export function createAuthenticatedRequest({
  baseURL,
  session,
  strictPaths = true,
  timeout = 15000,
  refreshBody = (refresh_token: string) => ({ refresh_token }),
}: {
  baseURL: string;
  session: SessionAdapter;
  strictPaths?: boolean;
  timeout?: number;
  refreshBody?: (token: string) => unknown;
}) {
  const transport = axios.create({ baseURL, timeout });
  let refreshFlight: { revision: number; promise: Promise<void> } | undefined;
  async function refreshSession(revision: number): Promise<void> {
    if (refreshFlight?.revision === revision) return refreshFlight.promise;
    const before = session.snapshot();
    if (!before.refreshToken || before.revision !== revision)
      throw new SessionChangedError();
    const refreshToken = before.refreshToken;
    const promise = (async () => {
      try {
        const response = await transport.post<unknown>(
          "/token/refresh",
          refreshBody(refreshToken),
        );
        if (session.snapshot().revision !== revision)
          throw new SessionChangedError();
        if (!session.isResponse(response.data)) {
          session.clear(revision);
          throw new Error("Ungültige Antwort beim Erneuern der Sitzung.");
        }
        if (!session.accept(response.data, revision))
          throw new SessionChangedError();
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          [400, 401, 403].includes(error.response?.status ?? 0)
        )
          session.clear(revision);
        throw error;
      }
    })();
    const flight = { revision, promise };
    refreshFlight = flight;
    try {
      await promise;
    } finally {
      if (refreshFlight === flight) refreshFlight = undefined;
    }
  }
  const request: ApiRequest = async <T>(
    options: AxiosRequestConfig,
    authenticated = true,
  ): Promise<T> => {
    const absolute =
      !options.url?.startsWith("/") || options.url.startsWith("//");
    if (
      (strictPaths || authenticated) &&
      (absolute || (options.baseURL && options.baseURL !== baseURL))
    )
      throw new Error("API-Pfade müssen relativ zur konfigurierten API sein.");
    const state = session.snapshot();
    const revision = state.revision;
    const sentToken = state.token;
    const headers = AxiosHeaders.from(
      options.headers as Parameters<typeof AxiosHeaders.from>[0],
    );
    if (authenticated && !sentToken) throw new SessionChangedError();
    if (authenticated) headers.set("Authorization", `Bearer ${sentToken}`);
    const send = () =>
      transport.request<T>({
        ...options,
        baseURL: options.baseURL || baseURL,
        headers,
      });
    try {
      const response = await send();
      if (authenticated && session.snapshot().revision !== revision)
        throw new SessionChangedError();
      return response.data;
    } catch (error) {
      if (
        !authenticated ||
        !axios.isAxiosError(error) ||
        error.response?.status !== 401 ||
        options.signal?.aborted
      )
        throw error;
      if (session.snapshot().revision !== revision)
        throw new SessionChangedError();
      // A delayed 401 may belong to a token another request has already refreshed.
      if (session.snapshot().token === sentToken)
        await refreshSession(revision);
      const current = session.snapshot();
      if (!current.token || current.revision !== revision)
        throw new SessionChangedError();
      headers.set("Authorization", `Bearer ${current.token}`);
      try {
        const response = await send();
        if (session.snapshot().revision !== revision)
          throw new SessionChangedError();
        return response.data;
      } catch (retryError) {
        if (
          axios.isAxiosError(retryError) &&
          retryError.response?.status === 401
        )
          session.clear(revision);
        throw retryError;
      }
    }
  };
  return { request, transport };
}
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409)
      return "Die Daten wurden zwischenzeitlich geändert oder ein Wert ist bereits vergeben. Bitte neu laden.";
    const data = error.response?.data as
      { error?: unknown; message?: unknown } | undefined;
    if (typeof data?.error === "string") return data.error;
    if (typeof data?.message === "string") return data.message;
    if (!error.response)
      return "Der Server ist derzeit nicht erreichbar. Bitte erneut versuchen.";
    if (error.response.status === 403)
      return "Dafür fehlt dir die Berechtigung.";
  }
  return error instanceof Error
    ? error.message
    : "Die Anfrage konnte nicht abgeschlossen werden.";
}
